import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ColorTheme } from '../models/user.model';
import { UserStorageService } from './user-storage.service';

export interface ThemeDefinition {
  id: ColorTheme;
  label: string;
  category: 'builtin' | 'custom';
  previewColor: string;
}

// Stored per user, not per browser: a personal theme belongs to whoever
// picked it.
const LS_KEY = 'color-theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private http     = inject(HttpClient);
  private userStorage = inject(UserStorageService);

  static readonly THEMES: ThemeDefinition[] = [
    { id: 'azure',    label: 'Azure',    category: 'builtin', previewColor: '#1565C0' },
    { id: 'rose',     label: 'Rose',     category: 'builtin', previewColor: '#C62828' },
    { id: 'magenta',  label: 'Magenta',  category: 'builtin', previewColor: '#AD1457' },
    { id: 'cyan',     label: 'Cyan',     category: 'builtin', previewColor: '#00838F' },
    { id: 'forest',   label: 'Forest',   category: 'custom',  previewColor: '#2E7D32' },
    { id: 'ocean',    label: 'Ocean',    category: 'custom',  previewColor: '#00695C' },
    { id: 'sunset',   label: 'Sunset',   category: 'custom',  previewColor: '#E65100' },
    { id: 'midnight', label: 'Midnight', category: 'custom',  previewColor: '#4527A0' },
  ];

  readonly activeTheme = signal<ColorTheme>('azure');
  readonly globalTheme = signal<ColorTheme>('azure');

  async init(): Promise<void> {
    // 1. Fetch global theme from backend (public endpoint, no auth)
    const global = await firstValueFrom(
      this.http.get<{ theme: string }>('/api/v1/config/theme').pipe(
        catchError(() => of({ theme: 'azure' }))
      )
    );
    const globalTheme = (global.theme ?? 'azure') as ColorTheme;
    this.globalTheme.set(globalTheme);

    // The user is not known yet: their personal theme is applied by
    // applyUserTheme(), which AuthService calls once the profile arrives.
    this.apply(globalTheme);
  }

  /** Personal theme of the user who just logged in, if they picked one. */
  applyUserTheme(): void {
    const userOverride = this.userStorage.get(LS_KEY) as ColorTheme | null;
    if (userOverride) this.apply(userOverride);
  }

  apply(theme: ColorTheme): void {
    const html = document.documentElement;
    ThemeService.THEMES.forEach(t => html.classList.remove(`theme-${t.id}`));
    if (theme !== 'azure') {
      html.classList.add(`theme-${theme}`);
    }
    this.activeTheme.set(theme);
  }

  setUserTheme(theme: ColorTheme): void {
    this.userStorage.set(LS_KEY, theme);
    this.apply(theme);
  }

  clearUserOverride(): void {
    this.userStorage.remove(LS_KEY);
    this.apply(this.globalTheme());
  }

  hasUserOverride(): boolean {
    return this.userStorage.get(LS_KEY) !== null;
  }

  async setGlobalTheme(theme: ColorTheme): Promise<void> {
    await firstValueFrom(
      this.http.post('/api/v1/admin/config/theme', { theme })
    );
    this.globalTheme.set(theme);
    // If user had no personal override, update visible theme
    if (!this.hasUserOverride()) {
      this.apply(theme);
    }
  }
}
